import { belongsTo, hasMany } from "@ember-data/model";
import { assert } from "@ember/debug";

import dayjs from "dayjs";
import {
  validateDate,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import BloodPressureMeasuredJournalEvent from "parog-web/models/blood-pressure-measured-journal-event";
import type BloodPressureSessionRequestedJournalEvent from "parog-web/models/blood-pressure-session-requested-journal-event";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import { sortBy } from "parog-web/utils/sort";
import tWithDefault from "parog-web/utils/t-with-default";
import { dayDiffBetweenDates } from "parog-web/utils/time";

//---- Automatically generated, don't update this part ----

export default class BloodPressureSessionStartedJournalEvent extends JournalEvent {
  static override type = "blood-pressure-session-started-journal-event";

  @hasMany("blood-pressure-measured-journal-event", {
    async: false,
    inverse: "bloodPressureSessionStartedJournalEvent"
  })
  declare bloodPressureMeasuredJournalEvents: BloodPressureMeasuredJournalEvent[];

  static override validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.blood-pressure-session-started-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée plus de 50 ans en avance. Veuillez modifier la date",
          onOrBefore: dayjs().add(50, "years").toDate()
        })
      ]
    };
  }

  //---- End of generated code ----

  @belongsTo("blood-pressure-session-requested-journal-event", {
    async: false,
    inverse: "bloodPressureSessionStartedJournalEvent"
  })
  declare bloodPressureSessionRequestedJournalEvent:
    | BloodPressureSessionRequestedJournalEvent
    | undefined;

  @belongsTo("patient", {
    async: false,
    inverse: "bloodPressureSessionStartedJournalEvents"
  })
  declare patient: Patient;

  get sortedBloodPressureMeasuredJournalEvents(): BloodPressureMeasuredJournalEvent[] {
    return this.bloodPressureMeasuredJournalEvents.toSorted(
      sortBy("occurredAt")
    );
  }

  override get isAlertingJournalEvent(): boolean {
    return (
      this.bloodPressureMeasuredJournalEvents
        .slice()
        .map(bp => bp.alertTriggeredJournalEvents)
        .flat().length > 0
    );
  }

  get meanSystolicPressure(): number | undefined {
    return _computeMeanMeasurements(
      this.bloodPressureMeasuredJournalEvents,
      "systolicPressure"
    );
  }

  get meanDiastolicPressure(): number | undefined {
    return _computeMeanMeasurements(
      this.bloodPressureMeasuredJournalEvents,
      "diastolicPressure"
    );
  }

  get meanHeartRate(): number | undefined {
    return _computeMeanMeasurements(
      this.bloodPressureMeasuredJournalEvents,
      "heartRate"
    );
  }

  /**
   * All the JE of today
   */
  get todayMeasurements(): BloodPressureMeasuredJournalEvent[] {
    return this.bloodPressureMeasuredJournalEvents.filter(
      je => je.sessionDay == this.currentSessionDay
    );
  }

  get hasCompletedAllMeasurementsOfTheDay(): boolean {
    return (
      this.todayMeasurements.length >=
      BloodPressureSessionStartedJournalEvent.MEASURE_BY_DAY
    );
  }

  /**
   *  An integer between zero and three representing the current day of the session
   */
  get currentSessionDay(): number {
    return dayDiffBetweenDates(this.occurredAt, new Date());
  }

  hasAlertForCode(code: "8480-6" | "8462-4"): boolean {
    return Boolean(
      this.bloodPressureMeasuredJournalEvents.find(bp =>
        bp.hasAlertForCode(code)
      )
    );
  }

  /**
   * Convert a Date into an integer corresponding to the sessionDay of this session
   * returns The session day corresponding to this session
   */
  getSessionDay(date: Date): number {
    return dayDiffBetweenDates(this.occurredAt, date);
  }

  /**
   * Return the date corresponding to the given day index
   */
  getDayDate(day: number): Date {
    const date = new Date(this.occurredAt);
    date.setDate(date.getDate() + day);
    return date;
  }

  /**
   * Return all the measurement for a specific session  day
   * sessionDay: An integer between zero and three representing the current day of the session
   */
  getMeasurementsOfSessionDay(
    sessionDay: number
  ): BloodPressureMeasuredJournalEvent[] {
    return this.bloodPressureMeasuredJournalEvents.filter(
      je => je.sessionDay == sessionDay
    );
  }

  getMorningMeasurementsOfSessionDay(
    sessionDay: number
  ): BloodPressureMeasuredJournalEvent[] {
    return this.getMeasurementsOfSessionDay(sessionDay).filter(
      m => m.isMorning
    );
  }

  getEveningMeasurementsOfSessionDay(
    sessionDay: number
  ): BloodPressureMeasuredJournalEvent[] {
    return this.getMeasurementsOfSessionDay(sessionDay).filter(
      m => !m.isMorning
    );
  }

  computeDateForMeasurement(sessionDay: number, isMorning: boolean): Date {
    const measureIndex = (
      isMorning
        ? this.getMorningMeasurementsOfSessionDay
        : this.getEveningMeasurementsOfSessionDay
    ).call(this, sessionDay).length;

    return BloodPressureMeasuredJournalEvent.createMeasurementDate(
      this.occurredAt,
      sessionDay,
      isMorning,
      measureIndex
    );
  }

  get measurementsDay1(): BloodPressureMeasuredJournalEvent[] {
    return this.getMeasurementsOfSessionDay(0);
  }

  get measurementsDay2(): BloodPressureMeasuredJournalEvent[] {
    return this.getMeasurementsOfSessionDay(1);
  }

  get measurementsDay3(): BloodPressureMeasuredJournalEvent[] {
    return this.getMeasurementsOfSessionDay(2);
  }

  get isOpened(): boolean {
    return this._isOpened(new Date());
  }

  /**
   * The begin of the day when the session starts
   */
  get beginDate(): Date {
    return dayjs(this.occurredAt).startOf("day").toDate();
  }

  /**
   * The exact date when the session finish
   */
  get endDate(): Date {
    return dayjs(this.beginDate)
      .add(this.sessionDuration - 1, "day")
      .endOf("day")
      .toDate();
  }

  /**
   * Return true if the session passed as parameter will overlap this session
   * A session lasts 3 days. Therefore, we check whether each day of the session is included in the existing session with day-granularity
   * @param date
   */
  isSessionStartDateMayOverlap(date: Date): boolean {
    return (
      dayjs(date).isAfter(dayjs(this.beginDate).subtract(3, "day"), "day") &&
      dayjs(date).isBefore(this.endDate)
    );
  }

  /**
   * Splitted for testing
   */
  _isOpened(nowDate: Date): boolean {
    const mSession = new Date(this.occurredAt);
    const mNow = new Date(nowDate);
    const dayDiff = dayDiffBetweenDates(mNow, mSession);
    assert("The session must occur before now", dayDiff >= 0);
    return dayDiff < BloodPressureSessionStartedJournalEvent.SESSION_DURATION;
  }

  /**
   * return True if it's the last day of the session. Meaning day 2 as we start at 0
   */
  get isLastDay(): boolean {
    return this.currentSessionDay >= 2;
  }

  get sessionDuration(): number {
    return BloodPressureSessionStartedJournalEvent.SESSION_DURATION;
  }

  /**
   * The duration of a session in days
   */
  static SESSION_DURATION = 3;

  /**
   */
  static MEASURE_BY_DAY = 6;
}

/****** PRIVATE ******/

function _computeMeanMeasurements(
  measurementJes: BloodPressureMeasuredJournalEvent[],
  propertyName: string
): number | undefined {
  const measures = measurementJes
    .map(je => je[propertyName])
    .filter(measure => measure != null);
  if (measures.length == 0) {
    return undefined;
  }
  const mean =
    measures.reduce((acc, measure) => acc + measure, 0) / measures.length;

  if (Number.isNaN(mean)) {
    return 0;
  }

  return mean;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "blood-pressure-session-started-journal-event": BloodPressureSessionStartedJournalEvent;
  }
}
